import { toCamelCase } from "~/shared/lib/backend-mapper";

import type { RequestContext } from "./client-types";

/** Сколько символов чужого не-JSON ответа оставляем в консоли — остальное шум. */
const RAW_ERROR_LOG_LIMIT = 200;

// ---------------------------------------------------------------------------
// Pure helpers extracted from `baseFetch` — no `fetch`, no auth side effects.
// Kept testable in isolation; `baseFetch` (client.ts) orchestrates them.
// ---------------------------------------------------------------------------

/** Map an HTTP status to the canonical `ApiError` code (matches backend-api.ts). */
export function mapStatusToCode(status: number): string {
    switch (status) {
        case 401:
            return "UNAUTHORIZED";
        case 403:
            return "FORBIDDEN";
        case 404:
            return "NOT_FOUND";
        case 409:
            return "CONFLICT";
        case 429:
            return "TOO_MANY_REQUESTS";
        default:
            return "INTERNAL_SERVER_ERROR";
    }
}

/**
 * Machine-readable code the backend put in the error body, if it sent one.
 *
 * The status alone cannot name the cause: one status answers several of them
 * (a refused price is 422 both for "no provider runs these inputs" and for any
 * other unprocessable request), and that is exactly what a caller needs to tell
 * apart. The backend says which in `code`; falling back to the status keeps the
 * previous behaviour for endpoints that send no body.
 */
export function extractErrorCode(errorText: string, status: number): string {
    try {
        const parsed = JSON.parse(errorText) as { code?: unknown };
        if (typeof parsed.code === "string" && parsed.code.length > 0) return parsed.code;
    } catch {
        // не JSON — код взять неоткуда, остаётся статус
    }
    return mapStatusToCode(status);
}

/**
 * Build request headers: JSON content-type, bearer auth and context headers.
 *
 * `X-User-Id` не отправляется намеренно. Личность backend берёт только из подписи:
 * этот заголовок он читает исключительно в паре с валидным внутренним `X-Api-Key`
 * (`flexible_auth_dependency`), которого у браузера нет, а `verified_request_identity`
 * заголовок от браузера игнорирует явно. Слать его — значит сообщать идентичность
 * со стороны клиента и лишний раз класть user id в логи промежуточных прокси.
 */
export function buildRequestHeaders(token: string, context?: RequestContext): Record<string, string> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    };
    if (context?.workspaceId) headers["X-Workspace-Id"] = context.workspaceId;
    if (context?.projectId) headers["X-Project-Id"] = context.projectId;
    if (context?.sceneId) headers["X-Scene-Id"] = context.sceneId;
    return headers;
}

/**
 * Derive a human-readable message from an error response body. Handles FastAPI's
 * three shapes: `{detail: string}`, `{detail: [{msg, loc}]}` (validation), `{message}`,
 * and falls back to the raw text when the body is not JSON.
 */
export function extractErrorMessage(errorText: string, fallback: string): string {
    try {
        const errorJson = JSON.parse(errorText) as {
            detail?: string | Array<{ msg: string; loc?: string[] }>;
            message?: string;
        };
        if (typeof errorJson.detail === "string") {
            return errorJson.detail;
        }
        if (Array.isArray(errorJson.detail)) {
            // FastAPI validation errors — extract first message with location
            const first = errorJson.detail[0];
            return first ? `${first.msg}${first.loc ? ` (${first.loc.join(" → ")})` : ""}` : fallback;
        }
        if (errorJson.message) {
            return errorJson.message;
        }
        return fallback;
    } catch {
        // Тело не разобралось как JSON — значит отвечал не наш backend, а промежуточное
        // звено (HTML-страница 502/504 от nginx, ответ WAF). Раньше этот текст уходил
        // в toast пользователю дословно и без ограничения длины; наружу отдаём общий
        // fallback, а сырое начало ответа оставляем в консоли для диагностики.
        if (errorText) {
            console.error("Non-JSON error body from the gateway:", errorText.slice(0, RAW_ERROR_LOG_LIMIT));
        }
        return fallback;
    }
}

/** Parse a successful response body: empty/non-JSON → undefined, JSON → camelCased payload. */
export async function parseJsonResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");
    if (!contentType?.includes("application/json")) {
        return undefined as T;
    }
    const data: unknown = await response.json();
    return toCamelCase<T>(data);
}
